import { Store } from '@reduxjs/toolkit';
import { getReplay } from '@sentry/react';
import { History } from 'history';
import { FC, StrictMode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { Route, Router } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { ThemeProvider } from 'styled-components';
import { QueryParamProvider } from 'use-query-params';

import Application from 'dashboard/components/Application';
import { CodeRunnerProvider } from 'dashboard/components/CodeRunner/CodeRunner';
import { DragAndDropProvider } from 'dashboard/components/DragAndDropProvider';
import ErrorState from 'dashboard/components/ErrorState';
import { serializeTheme } from 'dashboard/lib/theme';
import { isPersonerd } from 'dashboard/redux/selectors/current-user';
import store, { history, RootState } from 'dashboard/redux/store';
import Action from 'lib/interfaces/Action';
import { captureException } from 'lib/reporting';

// Import initializers for scripts that should run prior to the application being mounted.
import 'lib/initializers/axios';
import 'dashboard/lib/initializers/reporting';
import 'dashboard/lib/initializers/marketing';

interface ApplicationProps {
  // History and store as prop for testing purposes only.
  history?: History;
  store?: Store<RootState, Action>;
}

const ConnectedApplication: FC<ApplicationProps> = (props) => {
  const onError = (error: Error) => {
    captureException(error, 'fatal', error);

    if (isPersonerd(store.getState())) {
      getReplay()?.flush();
    }
  };

  return (
    <StrictMode>
      <ThemeProvider theme={serializeTheme()}>
        <ErrorBoundary FallbackComponent={ErrorState} onError={onError}>
          <HelmetProvider>
            <Helmet titleTemplate='%s - Persona' defaultTitle='Dashboard - Persona' />
            <Provider store={props.store || store}>
              <Router history={props.history || history}>
                <CompatRouter>
                  <QueryParamProvider ReactRouterRoute={Route}>
                    <DragAndDropProvider>
                      <CodeRunnerProvider>
                        <Application />
                      </CodeRunnerProvider>
                    </DragAndDropProvider>
                  </QueryParamProvider>
                </CompatRouter>
              </Router>
            </Provider>
          </HelmetProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </StrictMode>
  );
};

export default ConnectedApplication;
